// Whether a given row + field may be edited inline, and which field a Target Value save writes.
// Pure functions over OpportunityNode with no React and no design-system imports, so the branchy
// Target Value rules are unit-tested without mounting anything — the same isolation
// opportunityTableRows.ts and opportunityFilters.ts keep.
import {hasPermission, hasSomePermission} from '../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import type {CustomFieldColumnDef} from '../shared/components/custom-field-columns/customFieldColumnModel';
import type {OpportunityNode} from './opportunityTypes';

const TIME_AND_MATERIALS = 'TIME_AND_MATERIALS';
// Not a guess that happens to match the backend - these are the two values the backend itself calls
// fixed-price (BudgetLogic.isFixedPriceBudgetType), so a narrower branch would read null on rows the
// server was willing to serve. FIXED_PRICE alone is unreachable: the backend rewrites every create to
// FIXED_PRICE_V2 and refuses the reverse.
//
// This list is deliberately NOT the same set the server fills `budget` for. ProjectRecordMapper also
// fills it for a retainer whose periods are fixed-price, and that row is excluded here on purpose: a
// retainer's Target Value is the sum of its periods rather than one agreed price, so it keeps the
// computed baselineRevenue and stays read-only. Widening this to match the server would show a
// retainer the wrong number.
const FIXED_PRICE_TYPES: readonly string[] = ['FIXED_PRICE', 'FIXED_PRICE_V2'];

// Why a Target Value cell is read-only, so the cell can say so rather than just refusing to open.
export const TARGET_VALUE_READ_ONLY = {
	// Retainer and Non-billable rows display financialNumbers.baselineRevenue, a computed Financial
	// Service number derived from baseline roles and expenses. Nothing the client can write moves it: it
	// is absent from every GraphQL input and no mutation sets it, so an editable cell here would commit
	// and visibly do nothing. Read-only with an explanation is the honest surface. Fixed Price is not in
	// this set - it has `budget`, a writable source.
	CALCULATED: 'CALCULATED',
	// A T&M project not using a baseline has no baselineTarget to edit. Kept apart from the missing
	// end date below because they are different problems with different answers - telling someone to
	// set an end date when the real cause is the baseline toggle sends them to the wrong place.
	NO_BASELINE: 'NO_BASELINE',
	// T&M with a baseline but no end date: the same gate the Create Opportunity modal applies in
	// targetValueFieldState. baselineTarget needs an end date to mean anything.
	NO_END_DATE: 'NO_END_DATE',
} as const;

export type TargetValueReadOnly = (typeof TARGET_VALUE_READ_ONLY)[keyof typeof TARGET_VALUE_READ_ONLY];

export type TargetValueEdit = {editable: true} | {editable: false; reason: TargetValueReadOnly};

/**
 * The one gate for every inline editor: the same permission ProjectLogic.updateProjectById
 * validates. Without it the cells render exactly as they do today — no pointer, no hover tint, no
 * click target — instead of offering an edit the backend would reject.
 */
export const canEditRow = (): boolean => hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE);

/**
 * Client-side mirror of the server's canViewRevenueInfo, and the established web-front idiom for this
 * check (see showRevenue in PermissionsUtil).
 */
const canViewRevenue = (): boolean =>
	hasSomePermission([PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION, PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE]);

/**
 * Whether THIS row may be edited — three checks, and all three are load-bearing.
 *
 * PROJECTS_UPDATE is company-wide and says nothing about access to an individual project, so a project
 * the viewer holds read-only stays read-only however broad their permissions are — the same check the
 * expense list makes before letting a row be edited.
 *
 * Neither of those says anything about FINANCIAL access, and the two Target Value sources are gated on
 * it for reading but not for writing: `budget` and `baselineTarget` both resolve through a financial
 * authorise wrapper, while ProjectLogic.updateProjectById validates PROJECTS_UPDATE and project
 * membership and performs no revenue check anywhere. So without the third check a viewer lacking
 * revenue access is offered an editor over a blank cell and their save succeeds while the cell stays
 * blank — a silent no-op.
 *
 * That argument covers Target Value only. This predicate gates all five editable cells, so Salesperson,
 * Start/End Date, Win Probability and Stage go inert here too — and none of those four is revenue-gated
 * for reading (salesperson turns on project access; the win chance and the dates are plain fields). The
 * wider scope is the price of one rule over a per-column split, taken so that no single column ends up
 * obeying two permission rules.
 */
export const canEditOpportunity = (node: OpportunityNode): boolean => canEditRow() && !node.readOnlyAccess && canViewRevenue();

const hasEndDate = (node: OpportunityNode): boolean => !!(node.projectEndYear && node.projectEndMonth && node.projectEndDay);

/**
 * Which project field this row's Target Value lives in — the single source of truth for the branch.
 * `targetValueOf`, `targetValueCurrent`, `targetValueEdit` and the editor's commit all derive from this
 * one function, which is the mechanical form of the invariant it exists to keep: display source ==
 * edit source. They cannot disagree without this branch itself being wrong.
 *
 * `null` means there is no writable source — the cell displays the computed
 * financialNumbers.baselineRevenue and is read-only (Retainer, Non-billable).
 */
export const targetValueField = (node: OpportunityNode): 'budget' | 'baselineTarget' | null => {
	if (node.budgetType === TIME_AND_MATERIALS) {
		return 'baselineTarget';
	}
	if (node.budgetType && FIXED_PRICE_TYPES.includes(node.budgetType)) {
		return 'budget';
	}
	return null;
};

/**
 * Whether Target Value is editable on this row.
 *
 * Fixed Price is editable unconditionally: `budget` is a plain stored value, so unlike `baselineTarget`
 * it does not derive meaning from a date range and unlike `baselineRevenue` it is not recomputed from
 * anything — the T&M gates have no analogue, and adding one would only reintroduce a blank cell. An
 * unpriced row (null or 0) is blank but still editable: the editor is the only way to set a price from
 * the list, and a fourth read-only reason would need copy nobody asked for.
 */
export const targetValueEdit = (node: OpportunityNode): TargetValueEdit => {
	const field = targetValueField(node);
	if (field === 'budget') {
		return {editable: true};
	}
	if (field === null) {
		return {editable: false, reason: TARGET_VALUE_READ_ONLY.CALCULATED};
	}
	if (!node.useBaseline) {
		return {editable: false, reason: TARGET_VALUE_READ_ONLY.NO_BASELINE};
	}
	if (!hasEndDate(node)) {
		return {editable: false, reason: TARGET_VALUE_READ_ONLY.NO_END_DATE};
	}
	return {editable: true};
};

/** The stored value the editor pre-fills with — whichever field this row's Target Value lives in. */
export const targetValueCurrent = (node: OpportunityNode): number | null =>
	(targetValueField(node) === 'budget' ? node.budget : node.baselineTarget) ?? null;

/**
 * Whether a custom field cell may be edited: the same project-edit pair as every other cell, plus the
 * field's own read-only flag - and deliberately NOT canViewRevenue. canEditOpportunity's financial check
 * exists because Target Value's sources are revenue-gated for reading; custom fields are not financial
 * data, so requiring revenue access here would hide editors for no reason. The server applies the same
 * pair (UpdateCustomFieldValueMutation), so a cell is never offered for a save the backend rejects.
 */
export const canEditCustomField = (node: OpportunityNode, def: CustomFieldColumnDef): boolean =>
	canEditRow() && !node.readOnlyAccess && !def.readOnly;

/**
 * Whether a row's Labels cell may be edited: the same project-edit pair as every editor, and — like
 * custom fields, unlike Target Value — no revenue check. Labels are not financial data, so requiring
 * revenue access would wrongly lock out viewers who may edit the project (spec 010 D3). The server
 * validates PROJECTS_UPDATE and project membership on this same path (verifyPersonOnProject), so the
 * cell also needs writeAccessToProject: a salesperson who isn't on the team can see the row but not save.
 */
export const canEditLabels = (node: OpportunityNode): boolean =>
	canEditRow() && !node.readOnlyAccess && node.writeAccessToProject === true;
